/* Bamsedyra native search enhancements:
 * - typo tolerance via Fuse.js over a Liquid-generated product index (lazy, cached)
 * - recent searches (localStorage) + popular-category chips on empty focus
 * - quick add-to-cart from predictive dropdown rows
 * - "Mente du …?" suggestions in dropdown and on the zero-results page
 * All failures degrade silently to native predictive search.
 */
(function () {
  if (window.__bdSearchEnhance) return;
  window.__bdSearchEnhance = true;

  var script = document.currentScript || document.querySelector('script[data-index-url]');
  var FUSE_SRC = script && script.dataset.fuseSrc;
  var INDEX_URL = (script && script.dataset.indexUrl) || '/search?view=bd-index';
  var RECENT_KEY = 'bd_recent_searches';
  var CACHE_KEY = 'bd_search_idx_v1';
  var CACHE_TTL = 3600 * 1000; // 1 h
  var MIN_NATIVE_HITS = 4;

  var fuse = null;
  var indexPromise = null;

  /* ---------- helpers ---------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function storage(fn, fallback) {
    try { return fn(); } catch (e) { return fallback; }
  }

  /* ---------- index + Fuse (lazy) ---------- */
  function fetchPage(n) {
    var url = INDEX_URL + (INDEX_URL.indexOf('?') > -1 ? '&' : '?') + 'page=' + n;
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('index ' + r.status);
      return r.text();
    }).then(function (t) { return JSON.parse(t); });
  }

  function loadFuseLib() {
    if (window.Fuse) return Promise.resolve();
    if (!FUSE_SRC) return Promise.reject(new Error('no fuse src'));
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = FUSE_SRC;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function ensureIndex() {
    if (indexPromise) return indexPromise;
    indexPromise = (function () {
      var cached = storage(function () {
        var raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        var obj = JSON.parse(raw);
        if (!obj || Date.now() - obj.ts > CACHE_TTL) return null;
        return obj.items;
      }, null);

      var itemsPromise = cached
        ? Promise.resolve(cached)
        : fetchPage(1).then(function (first) {
            var pages = Math.min(first.pages || 1, 8);
            if (pages <= 1) return first.products;
            var rest = [];
            for (var i = 2; i <= pages; i++) rest.push(fetchPage(i));
            return Promise.all(rest).then(function (results) {
              var items = first.products;
              results.forEach(function (r) { items = items.concat(r.products); });
              return items;
            });
          }).then(function (items) {
            storage(function () {
              sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), items: items }));
            });
            return items;
          });

      return Promise.all([itemsPromise, loadFuseLib()]).then(function (res) {
        fuse = new window.Fuse(res[0], {
          keys: [
            { name: 't', weight: 0.55 },
            { name: 'g', weight: 0.2 },
            { name: 'd', weight: 0.15 },
            { name: 'v', weight: 0.1 }
          ],
          threshold: 0.34,
          ignoreLocation: true,
          minMatchCharLength: 2,
          includeScore: true
        });
        return fuse;
      });
    })().catch(function () { fuse = null; return null; });
    return indexPromise;
  }

  function fuzzyHits(query, excludeHandles, limit) {
    if (!fuse || !query) return [];
    var out = [];
    var results = fuse.search(query, { limit: 12 });
    for (var i = 0; i < results.length && out.length < limit; i++) {
      var p = results[i].item;
      if (excludeHandles && excludeHandles.has(p.h)) continue;
      out.push(p);
    }
    return out;
  }

  function badgeHTML(p) {
    if (!p.a) return '<span class="bd-ps-badge bd-ps-badge--soldout">Utsolgt</span>';
    if (p.o) return '<span class="bd-ps-badge bd-ps-badge--preorder">Forhåndsbestilling</span>';
    return '<span class="bd-ps-badge bd-ps-badge--stock">På lager</span>';
  }

  function rowHTML(p, id) {
    return (
      '<li id="' + id + '" class="predictive-search__list-item" role="option" aria-selected="false">' +
        '<a href="/products/' + esc(p.h) + '" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">' +
          (p.i ? '<img class="predictive-search__image" src="' + esc(p.i) + '" alt="" width="56" height="56" loading="lazy">' : '') +
          '<div class="predictive-search__item-content">' +
            '<p class="predictive-search__item-heading h6">' + esc(p.t) + '</p>' +
            '<div class="bd-ps-meta"><span class="bd-ps-price">' + esc(p.p) + '</span>' + badgeHTML(p) + '</div>' +
          '</div>' +
        '</a>' +
      '</li>'
    );
  }

  /* ---------- dropdown enhancement: top up thin native results ---------- */
  function enhanceResults(ps) {
    if (!fuse || !ps.input) return;
    var query = ps.input.value.trim();
    if (!query) return;
    var resultsEl = ps.querySelector('#predictive-search-results');
    if (!resultsEl || resultsEl.querySelector('.bd-ps-fuzzy')) return;

    var productsList = resultsEl.querySelector('#predictive-search-results-products-list');
    var nativeCount = productsList ? productsList.querySelectorAll('li').length : 0;
    if (nativeCount >= MIN_NATIVE_HITS) return;

    var existing = new Set();
    resultsEl.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      var m = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      if (m) existing.add(m[1]);
    });

    var hits = fuzzyHits(query, existing, MIN_NATIVE_HITS - nativeCount);
    if (!hits.length) return;

    if (productsList) {
      hits.forEach(function (p, i) {
        productsList.insertAdjacentHTML('beforeend', rowHTML(p, 'bd-ps-fuzzy-' + i));
      });
    } else {
      var group = document.createElement('div');
      group.className = 'bd-ps-fuzzy';
      group.innerHTML =
        '<h2 class="predictive-search__heading">Mente du …?</h2>' +
        '<ul class="predictive-search__results-list list-unstyled" role="group">' +
        hits.map(function (p, i) { return rowHTML(p, 'bd-ps-fuzzy-' + i); }).join('') +
        '</ul>';
      resultsEl.insertBefore(group, resultsEl.firstChild);
    }
  }

  /* ---------- mobile scroll fix for the always-visible bar dropdown ----------
   * The theme sizes the dropdown to "viewport height − header bottom", but the
   * search bar sits well below the header, so the panel overflows past the
   * bottom of the screen and its last rows (incl. the "view all" button) become
   * unreachable (it bounces instead of scrolling). Re-size the panel to fit from
   * its real on-screen top to the visible viewport bottom, honouring the mobile
   * keyboard via visualViewport, so it scrolls internally to the very bottom. */
  function isBarPS(ps) {
    return !!(ps && ps.classList && ps.classList.contains('bd-sbar__search'));
  }
  function fixPanelHeight(ps) {
    if (!isBarPS(ps)) return;
    var panel = ps.querySelector('[data-predictive-search]');
    if (!panel) return;
    var rect = panel.getBoundingClientRect();
    var vv = window.visualViewport;
    var availBottom = vv ? vv.height : window.innerHeight;
    var topRel = rect.top - (vv ? vv.offsetTop : 0);
    var max = availBottom - topRel - 12;
    if (max > 600) max = 600; // keep desktop dropdown tidy
    if (max < 220) max = 220;
    panel.style.maxHeight = Math.round(max) + 'px';
    panel.style.overflowY = 'auto';
  }

  function patchPredictiveSearch() {
    var PS = customElements.get('predictive-search');
    if (!PS || PS.__bdPatched) return;
    PS.__bdPatched = true;
    var orig = PS.prototype.renderSearchResults;
    PS.prototype.renderSearchResults = function (markup) {
      orig.call(this, markup);
      try { enhanceResults(this); } catch (e) { /* degrade silently */ }
      try { fixPanelHeight(this); } catch (e) { /* degrade silently */ }
    };
  }
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('predictive-search').then(patchPredictiveSearch).catch(function () {});
  }

  // keep the open bar dropdown fitted when the viewport changes (keyboard
  // open/close, orientation, resize)
  var rafPending = false;
  function scheduleRefix() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () {
      rafPending = false;
      document.querySelectorAll('predictive-search.bd-sbar__search[open]').forEach(function (ps) {
        try { fixPanelHeight(ps); } catch (e) {}
      });
    });
  }
  window.addEventListener('resize', scheduleRefix);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', scheduleRefix);
    window.visualViewport.addEventListener('scroll', scheduleRefix);
  }

  /* ---------- recent searches ---------- */
  function getRecent() {
    return storage(function () {
      var arr = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(arr) ? arr.slice(0, 5) : [];
    }, []);
  }
  function saveRecent(term) {
    term = (term || '').trim();
    if (!term) return;
    storage(function () {
      var arr = getRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
      arr.unshift(term);
      localStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, 5)));
    });
  }
  function clearRecent() {
    storage(function () { localStorage.removeItem(RECENT_KEY); });
  }

  /* ---------- empty-focus panel (recent searches + popular categories) ---------- */
  // Popular categories come from the section setting as "Label|/collections/handle"
  // entries joined with "§" (Liquid converts the textarea's newlines to §).
  function popularCats(ps) {
    return (ps.dataset.popularCats || '')
      .split('§')
      .map(function (s) {
        var i = s.indexOf('|');
        if (i < 0) return null;
        var label = s.slice(0, i).trim();
        var url = s.slice(i + 1).trim();
        return label && url ? { label: label, url: url } : null;
      })
      .filter(Boolean);
  }

  function showPanel(ps) {
    var container = ps.querySelector('[data-predictive-search]');
    if (!container) return;
    var recent = getRecent();
    var cats = popularCats(ps);
    if (!recent.length && !cats.length) return;

    var html = '<div id="predictive-search-results" role="listbox" class="bd-ps-panel">';
    if (recent.length) {
      html += '<h2 class="predictive-search__heading">Nylige søk</h2>' +
        '<ul class="bd-ps-recent" role="group">' +
        recent.map(function (t, i) {
          return '<li id="bd-ps-recent-' + i + '" role="option" aria-selected="false">' +
            '<a href="/search?q=' + encodeURIComponent(t) + '" tabindex="-1">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>' +
            esc(t) + '</a></li>';
        }).join('') +
        '</ul>' +
        '<button type="button" class="bd-ps-clear" data-bd-clear-recent>Tøm nylige søk</button>';
    }
    if (cats.length) {
      html += '<h2 class="predictive-search__heading">Populære kategorier</h2>' +
        '<ul class="bd-ps-chips" role="group">' +
        cats.map(function (c, i) {
          return '<li id="bd-ps-cat-' + i + '" role="option" aria-selected="false">' +
            '<a href="' + esc(c.url) + '" tabindex="-1">' + esc(c.label) + '</a></li>';
        }).join('') +
        '</ul>';
    }
    html += '</div>';
    container.innerHTML = html;
    try { ps.open(); } catch (e) { ps.setAttribute('open', 'true'); }
    try { fixPanelHeight(ps); } catch (e) {}
  }

  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches('predictive-search input[type="search"]')) return;
    ensureIndex(); // warm the fuzzy index on first focus
    if (!t.value.trim()) {
      var ps = t.closest('predictive-search');
      if (ps) showPanel(ps);
    }
  });

  // when the field is cleared while focused, bring the panel back after the
  // component's debounced close (300 ms) has run
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches('predictive-search input[type="search"]')) return;
    if (t.value.trim()) return;
    var ps = t.closest('predictive-search');
    setTimeout(function () {
      if (document.activeElement === t && !t.value.trim() && ps) showPanel(ps);
    }, 350);
  });

  /* ---------- record searches ---------- */
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || !f.matches) return;
    if (f.matches('form[role="search"]') || /\/search/.test(f.getAttribute('action') || '')) {
      var input = f.querySelector('input[name="q"]');
      if (input) saveRecent(input.value);
    }
  }, true);

  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('#predictive-search-results a[href*="/products/"], .predictive-search__search-for-button button');
    if (link) {
      var ps = link.closest('predictive-search');
      var input = ps && ps.querySelector('input[type="search"]');
      if (input) saveRecent(input.value);
    }
    var clear = e.target.closest && e.target.closest('[data-bd-clear-recent]');
    if (clear) {
      e.preventDefault();
      clearRecent();
      var ps2 = clear.closest('predictive-search');
      if (ps2) showPanel(ps2);
    }
  });

  /* ---------- quick add from dropdown ---------- */
  function refreshCartBubble() {
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(function (el) {
        el.textContent = cart.item_count;
      });
    }).catch(function () {});
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-bd-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (btn.classList.contains('is-loading') || btn.classList.contains('is-added')) return;
    btn.classList.add('is-loading');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(btn.dataset.bdQuickAdd), quantity: 1 })
    }).then(function (r) {
      if (!r.ok) throw new Error('add ' + r.status);
      return r.json();
    }).then(function () {
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      btn.innerHTML = '✓';
      btn.setAttribute('aria-label', 'Lagt i handlekurven');
      refreshCartBubble();
    }).catch(function () {
      btn.classList.remove('is-loading');
      var prev = btn.innerHTML;
      btn.innerHTML = '!';
      btn.setAttribute('aria-label', 'Noe gikk galt – prøv igjen');
      setTimeout(function () { btn.innerHTML = prev; }, 2000);
    });
  });

  /* ---------- zero-results page: "Mente du …?" ---------- */
  function initZeroResults() {
    var zero = document.querySelector('[data-bd-search-zero]');
    if (!zero) return;
    var q = zero.getAttribute('data-q');
    if (!q) return;
    ensureIndex().then(function () {
      if (!fuse) return;
      var hits = fuzzyHits(q, null, 3);
      if (!hits.length) return;
      var wrap = zero.querySelector('[data-bd-zero-suggest]');
      var list = wrap && wrap.querySelector('ul');
      if (!wrap || !list) return;
      list.innerHTML = hits.map(function (p) {
        return '<li><a href="/products/' + esc(p.h) + '">' +
          (p.i ? '<img src="' + esc(p.i) + '" alt="" width="48" height="48" loading="lazy">' : '') +
          '<span><p class="bd-zero__suggest-title">' + esc(p.t) + '</p>' +
          '<span class="bd-ps-meta"><span class="bd-ps-price">' + esc(p.p) + '</span>' + badgeHTML(p) + '</span></span>' +
          '</a></li>';
      }).join('');
      wrap.hidden = false;
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initZeroResults);
  } else {
    initZeroResults();
  }
})();
